import {
  appendRow,
  deleteRow,
  ensureTab,
  getRows,
  updateRow,
} from '../lib/googleSheets';

export const AVAILABILITY_TAB = 'Availability';
export const AVAILABILITY_HEADERS = [
  'id',
  'date',
  'year',
  'month',
  'name',
  'email',
  'phone',
  'shift',
  'created_at',
  'updated_at',
];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function pad(value) {
  return String(value).padStart(2, '0');
}

function formatParts(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

// Google Sheets may return dates as ISO text, M/D/YYYY, a serial number, or
// a long-form string. Convert every variant to YYYY-MM-DD.
export function normalizeDate(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';

  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return formatParts(iso[1], iso[2], iso[3]);

  const slash = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slash) {
    let first = Number(slash[1]);
    let second = Number(slash[2]);
    let year = Number(slash[3]);
    if (year < 100) year += 2000;
    // Assume M/D/YYYY unless the first part can only be a day.
    if (first > 12 && second <= 12) {
      [first, second] = [second, first];
    }
    return formatParts(year, first, second);
  }

  if (/^\d{5}(\.\d+)?$/.test(text)) {
    const serialDate = new Date(Date.UTC(1899, 11, 30) + Number(text) * 86400000);
    return formatParts(
      serialDate.getUTCFullYear(),
      serialDate.getUTCMonth() + 1,
      serialDate.getUTCDate(),
    );
  }

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return formatParts(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
  }

  return text;
}

function deriveShift(date) {
  const day = new Date(`${date}T12:00:00`);
  if (Number.isNaN(day.getTime())) return '';
  return day.getDay() === 0 || day.getDay() === 6 ? 'Afternoon' : 'Evening / night';
}

function rowToAvailability(row) {
  const record = AVAILABILITY_HEADERS.reduce(
    (result, header, index) => ({ ...result, [header]: row?.[index] ?? '' }),
    {},
  );

  record.date = normalizeDate(record.date);
  record.name = String(record.name || '').trim();

  const parsedDate = new Date(`${record.date}T12:00:00`);
  if (!Number.isNaN(parsedDate.getTime())) {
    record.year = record.year || String(parsedDate.getFullYear());
    record.month = record.month || MONTH_NAMES[parsedDate.getMonth()];
    record.shift = record.shift || deriveShift(record.date);
  }

  return record;
}

function availabilityToRow(record) {
  return AVAILABILITY_HEADERS.map((header) => record[header] ?? '');
}

async function findAvailabilityRow(id) {
  const rows = await getRows(`${AVAILABILITY_TAB}!A:A`);
  const index = rows.findIndex(
    (row, rowIndex) => rowIndex > 0 && String(row?.[0] || '') === String(id),
  );

  if (index < 0) {
    return null;
  }

  return { index, sheetRow: index + 1 };
}

export async function listAvailability() {
  await ensureTab(AVAILABILITY_TAB, AVAILABILITY_HEADERS);
  const rows = await getRows(`${AVAILABILITY_TAB}!A2:Z`);
  const records = [];

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    const hasContent = row && row.some((cell) => String(cell ?? '').trim());
    if (!hasContent) continue;

    const record = rowToAvailability(row);
    if (!record.date || !record.name) continue;

    if (!record.id) {
      // Row was typed into the sheet by hand: give it an id so it can be
      // displayed and removed from the calendar.
      const sheetRow = index + 2;
      const newId = crypto.randomUUID();
      try {
        await updateRow(`${AVAILABILITY_TAB}!A${sheetRow}`, [newId]);
        record.id = newId;
      } catch {
        record.id = `row-${sheetRow}`;
      }
    }

    records.push(record);
  }

  return records;
}

export async function getAvailabilityRecord(id) {
  await ensureTab(AVAILABILITY_TAB, AVAILABILITY_HEADERS);
  const location = await findAvailabilityRow(id);
  if (!location) {
    return null;
  }

  const rows = await getRows(
    `${AVAILABILITY_TAB}!A${location.sheetRow}:Z${location.sheetRow}`,
  );
  return rows[0] ? rowToAvailability(rows[0]) : null;
}

export async function createAvailabilityRecord(record) {
  await ensureTab(AVAILABILITY_TAB, AVAILABILITY_HEADERS);
  const now = new Date().toISOString();
  const fullRecord = {
    id: crypto.randomUUID(),
    date: record.date || '',
    year: String(record.year || ''),
    month: record.month || '',
    name: record.name || '',
    email: record.email || '',
    phone: record.phone || '',
    shift: record.shift || '',
    created_at: now,
    updated_at: now,
  };

  await appendRow(`${AVAILABILITY_TAB}!A:Z`, availabilityToRow(fullRecord));
  return fullRecord;
}

export async function createAvailabilityRecords(records) {
  const saved = [];
  for (const record of records) {
    saved.push(await createAvailabilityRecord(record));
  }
  return saved;
}

export async function updateAvailabilityRecord(id, changes) {
  await ensureTab(AVAILABILITY_TAB, AVAILABILITY_HEADERS);
  const location = await findAvailabilityRow(id);
  if (!location) {
    throw new Error(`Availability record not found: ${id}`);
  }

  const existing = await getAvailabilityRecord(id);
  if (!existing) {
    throw new Error(`Availability record not found: ${id}`);
  }

  const updatedRecord = {
    ...existing,
    ...changes,
    id: existing.id,
    created_at: existing.created_at,
    updated_at: new Date().toISOString(),
  };

  await updateRow(
    `${AVAILABILITY_TAB}!A${location.sheetRow}:Z${location.sheetRow}`,
    availabilityToRow(updatedRecord),
  );
  return updatedRecord;
}

export async function deleteAvailabilityRecord(id) {
  await ensureTab(AVAILABILITY_TAB, AVAILABILITY_HEADERS);
  const location = await findAvailabilityRow(id);
  if (!location) {
    throw new Error(`Availability record not found: ${id}`);
  }

  await deleteRow(AVAILABILITY_TAB, id);

  const remaining = await getRows(`${AVAILABILITY_TAB}!A:A`);
  const stillExists = remaining.some(
    (row, index) => index > 0 && String(row?.[0] || '') === String(id),
  );
  if (stillExists) {
    throw new Error('The availability entry was not removed');
  }

  return { id, deleted: true };
}